import { createContext, useContext, useEffect, useState } from "react";
import * as z from "zod";
import { isValidCPF } from "../../../utils/cpfValidator";
import { isValidRG } from "../../../utils/rgValidator";
import { isValidCarPlate } from "../../../utils/carPlateValidator";
import { clamp } from "../../../utils/math";
import { NotificationManager } from "react-notifications";
import { wait } from "../../../utils/timer";
import { format } from "date-fns";
import { useAuth } from "../../../contexts/auth";
import ImagesService from "../../../services/ImagesService";
import { sanitizeNumeric, slugify } from "../../../utils/string";
import CondoUserService from "../../../services/CondoUserService";
import { useUserStore } from "../../../contexts/userStore";
import TagService from "../../../services/TagService";
import { useTranslation } from "react-i18next";

const defaultState = {
   id: null,
   open: false,
   tagName: null,
   tagIcon: null,
   createUser: false,
   totalSteps: 4,
   step: 1,
   urlImg: null,
   photoFile: null,
   name: "",
   cpf: "",
   rg: "",
   phoneNumber: "",
   plate: "",
   description: "",
   selectedTag: null,
   credentials: [],
   unit: null,
   responsibility: "0",
   relation: "0",
   otherRelation: "",
   permanencyTime: 0,
   customEndDate: null,
   info: "",
   isSubmitting: false,
   findUserByUuid: null,
   errors: {},
};

const FastFormCondoUserContext = createContext({
   ...defaultState,
   update: () => { },
   prevStep: () => { },
   nextStep: () => { },
   setStep: (step) => { },
   save: () => { },
   findUserByUuid: (uuid) => { }
});

const initialErrorsState = {
   name: [],
   cpf: [],
   rg: [],
   phoneNumber: [],
   plate: [],
};

export function FastFormCondoUserProvider({ children }) {
   const { user } = useAuth();
   const { selectedLicenceObj } = useUserStore();
   const [id, setId] = useState();
   const [open, setOpen] = useState(false);
   const [step, setStep] = useState(1);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [isLoadingUser, setIsLoadingUser] = useState(false);
   const [totalSteps, setTotalSteps] = useState(4);
   const [createUser, setCreateUser] = useState(false);
   const [urlImg, setUrlImg] = useState(null);
   const [photoFile, setPhotoFile] = useState(null);
   const [name, setName] = useState("");
   const [cpf, setCpf] = useState("");
   const [rg, setRg] = useState("");
   const [phoneNumber, setPhoneNumber] = useState("");
   const [plate, setPlate] = useState("");
   const [description, setDescription] = useState("");
   const [selectedTag, setSelectedTag] = useState(null);
   const [credentials, setCredentials] = useState([]);
   const [unit, setUnit] = useState(null);
   const [responsibility, setResponsibility] = useState("0");
   const [relation, setRelation] = useState("0");
   const [otherRelation, setOtherRelation] = useState("");
   const [permanencyTime, setPermanencyTime] = useState(0);
   const [customEndDate, setCustomEndDate] = useState(null);
   const [info, setInfo] = useState("");
   const [errors, setErrors] = useState(initialErrorsState);
   const [tagName, setTagName] = useState(null);
   const [tagIcon, setTagIcon] = useState(null);
   const { t } = useTranslation();

   const setters = {
      open: setOpen,
      step: setStep,
      tagName: setTagName,
      tagIcon: setTagIcon,
      totalSteps: setTotalSteps,
      createUser: setCreateUser,
      urlImg: setUrlImg,
      photoFile: setPhotoFile,
      name: setName,
      cpf: setCpf,
      rg: setRg,
      phoneNumber: setPhoneNumber,
      plate: setPlate,
      description: setDescription,
      selectedTag: setSelectedTag,
      credentials: setCredentials,
      unit: setUnit,
      responsibility: setResponsibility,
      relation: setRelation,
      otherRelation: setOtherRelation,
      permanencyTime: setPermanencyTime,
      customEndDate: setCustomEndDate,
      info: setInfo,
      id: setId,
   };

   function validateStepUserData() {
      const userDataSchema = z.preprocess(
         (value) => {
            if (typeof value !== "object" || value === null) return value;

            return Object.fromEntries(
               Object.entries(value).map(([key, value]) => [
                  key,
                  value === "" ? null : value,
               ])
            );
         },
         z.object({
            name: z.string({ error: t("monitoring.invalidName") })
               .min(3, { error: t("monitoring.invalidName") })
               .includes(' ', { error: t("monitoring.fullNameRequired") }),
            cpf: z.string()
               .refine(isValidCPF, { error: t("monitoring.invalidCpf") })
               .nullish(),
            rg: z.string()
               .refine(isValidRG, { error: t("monitoring.invalidRg") })
               .nullish(),
            phoneNumber: z
               .string()
               .regex(/^[\d\s()+-]+$/, t("monitoring.invalidPhone"))
               .min(10, t("monitoring.shortPhone"))
               .nullish(),
            plate: z.string()
               .refine(isValidCarPlate, { error: t("monitoring.invalidPlate") })
               .nullish(),
         })
      );

      try {
         userDataSchema.parse({
            name,
            cpf,
            rg,
            phoneNumber,
            plate
         });
         return true;
      } catch (error) {
         if (error instanceof z.ZodError) {
            const issues = {
               name: [],
               cpf: [],
               rg: [],
               phoneNumber: [],
               plate: [],
            };
            for (let issue of error.issues) {
               issues[issue.path[0]].push(issue.message);
            }
            setErrors(prev => ({
               ...prev,
               ...issues,
            }));
            return false;
         }
      }
   }

   useEffect(() => {
      (async () => {
         if (selectedTag) {
            const { data: tag } = await TagService.getTag(selectedTag);
            setTagName(tag.name);
            setTagIcon(tag.icon);
            nextStep();
         }
      })()
   }, [selectedTag]);

   function validateStepProfile() {
      const isValid = !!selectedTag;
      if (!isValid) {
         setErrors(prev => ({
            ...prev,
            selectedTag: [t("monitoring.selectTagRequired")]
         }))
      }
      return isValid;
   }

   function validateStepCredentials() {
      const isValid = !createUser || (!!urlImg || credentials?.length > 0);
      if (!isValid) {
         setErrors(prev => ({
            ...prev,
            credentials: [t("monitoring.credentialRequired")],
         }));
      }
      return isValid;
   }

   function validateStepUnit() {
      return true;
   }

   function validateStepAccess() {
      const issues = [];
      if (permanencyTime === 9) {
         if (!customEndDate) {
            issues.push(t("monitoring.mustFill"));
         }
         if (new Date(customEndDate).getTime() <= new Date().getTime()) {
            issues.push(t("monitoring.mustInFuture"));
         }
      }
      const isValid = issues.length === 0;
      if (!isValid) {
         setErrors(prev => ({
            ...prev,
            customEndDate: issues,
         }))
      }
      return isValid;
   }

   function reset() {
      setErrors(initialErrorsState);
      Object.entries(defaultState).forEach(([field, value]) => {
         if (field !== "open" && setters[field]) {
            setters[field](value);
         }
      });
   }

   function update(field, value) {
      if (setters[field]) {
         setters[field](value);
         if (errors[field] && errors[field].length > 0) {
            setErrors(prev => ({
               ...prev,
               [field]: [],
            }));
         }
         if (field === 'urlImg' && errors.credentials?.length > 0) {
            setErrors(prev => ({
               ...prev,
               credentials: [],
            }));
         }
         if (field === "open" && value === false) {
            reset();
         }
         if (field === "createUser") {
            setTotalSteps(value === true ? 5 : 4);
         }
      } else {
         console.warn(`Campo "${field}" não existe no contexto.`);
      }
   }

   const validators = createUser ? [
      validateStepUserData,
      validateStepProfile,
      validateStepCredentials,
      validateStepUnit,
      validateStepAccess,
   ] : [
      validateStepUserData,
      validateStepProfile,
      validateStepUnit,
      validateStepAccess,
   ];

   function prevStep() {
      setStep(prev => Math.max(1, prev - 1));
   }

   function validateSteps(to) {
      let isValid = true;
      for (let i = 0; i <= (to - 1); i++) {
         if (isValid) {
            isValid = validators[i]();
         }
      }
      return isValid;
   }

   function updateStep(step) {
      setStep(prev => {
         let stepTmp = clamp(1, totalSteps, step(prev));
         if (validateSteps(stepTmp - 1)) {
            return stepTmp;
         } else {
            NotificationManager.warning("", t("monitoring.checkPreviousSteps"));
         }
         return prev;
      });
   }

   function nextStep() {
      updateStep(prev => prev + 1);
   }

   function changeStep(step) {
      updateStep(prev => step);
   }

   async function handleImportPhotoSubmission() {
      let newUrl = urlImg;

      if (photoFile != null) {
         const now = new Date();
         const date = format(now, "yyyyMMddHHmmss");
         newUrl = `device-images/${selectedLicenceObj?.SN_equipment}/${date}_${slugify(name)}/${slugify(name)}.jpeg`;
         const formData = new FormData();
         formData.append("file", photoFile);
         formData.append("imageUrl", newUrl);
         formData.append("entity", createUser ? "user" : "access");
         formData.append("organizationExternalId", user.userData.selectedOrganization.externalId);
         formData.append("serialNumber", selectedLicenceObj?.SN_equipment);
         try {
            const response = await ImagesService.storeImage(formData);
            setUrlImg(response.data.payload.id);
            return response.data.payload.id;
         } catch (e) {
            console.error(e);
            setIsSubmitting(false);
            throw e;
         }
      } else {
         return newUrl;
      }
   }

   function buildPayload(urlFromResponse) {
      let payload = {
         id,
         createUser,
         name,
         cpf,
         rg,
         urlImg: urlFromResponse,
         phoneNumber,
         plate,
         description,
         tag: selectedTag,
         credentials,
         unit: unit?.externalId,
         responsibility,
         relation,
         otherRelation,
         permanencyTime,
         customEndDate,
         info,
         license_id: selectedLicenceObj?.id,
      }

      if (createUser) {
         payload.credentials = credentials;
      }

      return payload;
   }

   function parseUnitData(unitdata) {
      if (!unitdata) {
         return null;
      }
      let payload = unitdata;
      payload.id_unit = unitdata?.id;
      payload.name = unitdata.name;
      payload.parkingLotBusy = unitdata.metadata?.find(metadataItem => metadataItem.name === 'parkingLotBusy')?.value;
      payload.parkingLotQuantity = unitdata.metadata?.find(metadataItem => metadataItem.name === 'parkingLotQuantity')?.value;
      return payload;
   }

   async function save() {
      if (validateSteps(totalSteps)) {
         setIsSubmitting(true);
         console.log('salvando...');

         const urlFromResponse = await handleImportPhotoSubmission();
         let payload = buildPayload(urlFromResponse);
         const response = await CondoUserService.createAccess(payload);

         if (response.data?.id) {
            reset();
            setOpen(false);
            console.log('salvo.');
         }

         setIsSubmitting(false);
      } else {
         NotificationManager.warning("", t("monitoring.checkPreviousSteps"));
      }
   }

   async function findUser(document) {
      if (!document || document.length < 3) {
         NotificationManager.warning("", "Digite ao menos 3 dígitos do documento.");
         return;
      }

      try {
         setIsLoadingUser(true);
         const { data: user, status } = await CondoUserService.getByCondoAndDocument(
            sanitizeNumeric(document),
            selectedLicenceObj.id_condominium
         );

         if (status === 200 && !!user) {
            const userLicenseData = user.licenses?.find(license => license.ownerUnitId === selectedLicenceObj.id_condominium);

            setId(user.id);
            setUrlImg(user.url_photo);
            setName(user.name);
            setCpf(user.cpf);
            setRg(user.rg);
            setPhoneNumber(user.phone_number);
            setCredentials(user.credentials);
            setSelectedTag(user.tags?.find(tag => tag.ownerUnitId === selectedLicenceObj.id_condominium)?.tagId);
            update("unit", user.unit);
            setResponsibility(userLicenseData?.responsibility);
            setRelation(userLicenseData?.relation);
            setOtherRelation(userLicenseData?.otherRelation);
            setInfo(userLicenseData?.tempAccessInfo);
            update("createUser", true);
            setErrors(initialErrorsState);
         }
      } catch (e) {
         console.error(e);
      } finally {
         setIsLoadingUser(false);
      }
   }

   async function findUserByUuid(uuid) {
      if (!uuid) {
         NotificationManager.warning("", "UUID inválido.");
         return;
      }

      try {
         setIsLoadingUser(true);
         const { data: user, status } = await CondoUserService.getByCondoAndUuid(
            uuid,
            selectedLicenceObj.id_condominium
         );

         if (status === 200 && !!user) {
            const userLicenseData = user.licenses?.find(license => license.ownerUnitId === selectedLicenceObj.id_condominium);

            setId(user.id);
            setUrlImg(user.url_photo);
            setName(user.name);
            setCpf(user.cpf);
            setRg(user.rg);
            setPhoneNumber(user.phone_number);
            setCredentials(user.credentials);
            setSelectedTag(user.tags?.find(tag => tag.ownerUnitId === selectedLicenceObj.id_condominium)?.tagId);
            update("unit", user.unit);
            setResponsibility(userLicenseData?.responsibility);
            setRelation(userLicenseData?.relation);
            setOtherRelation(userLicenseData?.otherRelation);
            setInfo(userLicenseData?.tempAccessInfo);
            update("createUser", true);
            setErrors(initialErrorsState);
         }
      } catch (e) {
         console.error(e);
      } finally {
         setIsLoadingUser(false);
      }
   }

   return (
      <FastFormCondoUserContext.Provider
         value={{
            open,
            step,
            totalSteps,
            createUser,
            urlImg,
            photoFile,
            name,
            cpf,
            rg,
            phoneNumber,
            plate,
            description,
            selectedTag,
            credentials,
            unit,
            tagName,
            tagIcon,
            responsibility,
            relation,
            otherRelation,
            permanencyTime,
            customEndDate,
            info,
            errors,
            isSubmitting,
            findUser,
            findUserByUuid,
            isLoadingUser,
            id,

            parseUnitData,
            setStep: changeStep,
            prevStep,
            nextStep,
            update,
            save,
         }}
      >
         {children}
      </FastFormCondoUserContext.Provider>
   );
}

export function useFastCondoUserStore() {
   return useContext(FastFormCondoUserContext);
}